import { useState } from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';
import { Checkbox, Select, TextField } from 'Components/ui';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BORDER = 'var(--vo-steel)';
const TEXT = 'var(--vo-ink-2)';
const DIVIDER = 'var(--vo-cloud)';
const CHIP_BG = 'var(--vo-capri-50)';

const Card = styled.div`
  max-width: 800px;
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${DIVIDER};
`;

const IconTile = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--vo-escuro), var(--vo-cobalto));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SectionTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;

const SectionSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${p => p.fullWidth && 'grid-column: 1 / -1;'}
`;

const Label = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${TEXT};
`;

const ChipInputWrapper = styled.div`
  min-height: 40px;
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  padding: 8px 12px;
  background: white;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  box-sizing: border-box;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: ${CHIP_BG};
  color: ${BLUE};
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 20px;
`;

const ChipX = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${BLUE};
  line-height: 1;
  display: flex;
  align-items: center;
`;

const ChipInputInner = styled.input`
  border: none;
  outline: none;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${INK};
  background: transparent;
  flex: 1;
  min-width: 100px;
`;


const CheckboxRow = styled.div`
  grid-column: 1 / -1;
  display: flex;
  gap: 28px;
  padding: 14px 0;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
`;

const CheckboxTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${TEXT};
  margin: 0 0 2px;
`;

const CheckboxDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`;

const ErrorText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-danger);
`;

const MonitorIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/>
    <line x1="8" y1="21" x2="16" y2="21"/>
    <line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
);

const XIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const TagInput = ({ value, onChange, placeholder }) => {
  const [inputVal, setInputVal] = useState('');
  const tags = Array.isArray(value) ? value : [];

  const addTag = (raw) => {
    const tag = raw.trim();
    if (tag && !tags.includes(tag)) onChange([...tags, tag]);
    setInputVal('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(inputVal); }
    if (e.key === 'Backspace' && !inputVal && tags.length) onChange(tags.slice(0, -1));
  };

  return (
    <ChipInputWrapper>
      {tags.map((tag, i) => (
        <Chip key={i}>
          {tag}
          <ChipX type="button" onClick={() => onChange(tags.filter((_, idx) => idx !== i))}>
            <XIcon />
          </ChipX>
        </Chip>
      ))}
      <ChipInputInner
        value={inputVal}
        onChange={e => setInputVal(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => inputVal && addTag(inputVal)}
        placeholder={tags.length === 0 ? (placeholder || '') : ''}
      />
    </ChipInputWrapper>
  );
};

export const Detalhes = ({ register, control, errors, users, id, t }) => {
  const getCriticityLabel = level => t(`mapping:systems.criticality.${level}`, { defaultValue: level });

  return (
    <Card>
      <SectionHead>
        <IconTile><MonitorIcon /></IconTile>
        <div>
          <SectionTitle>{t('mapping:systems.form.detailsSectionTitle')}</SectionTitle>
          <SectionSubtitle>{t('mapping:systems.form.detailsSectionSubtitle')}</SectionSubtitle>
        </div>
      </SectionHead>

      <FormGrid>
        <FieldGroup fullWidth>
          <Label>{t('mapping:systems.form.fields.name')} *</Label>
          <TextField
            fullWidth
            {...register('name', { required: t('mapping:systems.form.validation.nameRequired') })}
            placeholder={t('mapping:systems.form.namePlaceholder')}
            error={!!errors?.name}
            disabled={!!id}
          />
          {errors?.name && <ErrorText>{errors.name.message}</ErrorText>}
        </FieldGroup>

        <FieldGroup>
          <Label>{t('mapping:systems.form.fields.criticality')} *</Label>
          <Controller
            control={control}
            name="criticity"
            rules={{ required: t('mapping:systems.form.validation.criticalityRequired') }}
            render={({ field: { onChange, value } }) => (
              <Select
                name="criticity"
                value={value}
                onChange={onChange}
                options={[
                  { value: '', label: t('mapping:systems.form.placeholders.selectCriticality') },
                  ...['1','2','3','4','5'].map(l => ({ value: l, label: getCriticityLabel(l) })),
                ]}
              />
            )}
          />
          {errors?.criticity && <ErrorText>{errors.criticity.message}</ErrorText>}
        </FieldGroup>

        <FieldGroup>
          <Label>{t('mapping:systems.form.fields.responsible')} *</Label>
          <Controller
            control={control}
            name="user"
            rules={{ required: t('mapping:systems.form.validation.responsibleRequired') }}
            render={({ field: { onChange, value } }) => (
              <Select
                name="user"
                value={value}
                onChange={onChange}
                options={[
                  { value: '', label: t('mapping:systems.form.placeholders.selectResponsible') },
                  ...(users || []).map(u => ({ value: u.name, label: u.name })),
                ]}
              />
            )}
          />
          {errors?.user && <ErrorText>{errors.user.message}</ErrorText>}
        </FieldGroup>

        <FieldGroup>
          <Label>{t('mapping:systems.form.fields.tags')}</Label>
          <Controller
            control={control}
            name="tags"
            render={({ field: { onChange, value } }) => (
              <TagInput
                value={value}
                onChange={onChange}
                placeholder={t('mapping:systems.form.placeholders.tags')}
              />
            )}
          />
        </FieldGroup>

        <FieldGroup>
          <Label>{t('mapping:systems.form.fields.database')}</Label>
          <TextField
            fullWidth
            {...register('dataBase')}
            placeholder={t('mapping:systems.form.dataBasePlaceholder')}
          />
        </FieldGroup>

        <CheckboxRow>
          <Controller
            control={control}
            name="saas"
            render={({ field: { onChange, value } }) => (
              <CheckboxLabel>
                <Checkbox
                  checked={value}
                  onChange={onChange}
                />
                <div>
                  <CheckboxTitle>{t('mapping:systems.form.saasTitle')}</CheckboxTitle>
                  <CheckboxDesc>{t('mapping:systems.form.saasDesc')}</CheckboxDesc>
                </div>
              </CheckboxLabel>
            )}
          />
          <Controller
            control={control}
            name="developedInHouse"
            render={({ field: { onChange, value } }) => (
              <CheckboxLabel>
                <Checkbox
                  checked={value}
                  onChange={onChange}
                />
                <div>
                  <CheckboxTitle>{t('mapping:systems.form.developedInHouseTitle')}</CheckboxTitle>
                  <CheckboxDesc>{t('mapping:systems.form.developedInHouseDesc')}</CheckboxDesc>
                </div>
              </CheckboxLabel>
            )}
          />
        </CheckboxRow>

        <FieldGroup fullWidth>
          <Label>{t('mapping:systems.form.fields.description')} *</Label>
          <TextField
            multiline
            fullWidth
            {...register('description', { required: t('mapping:systems.form.validation.descriptionRequired') })}
            rows={3}
            placeholder={t('mapping:systems.form.descriptionPlaceholder')}
            error={!!errors?.description}
          />
          {errors?.description && <ErrorText>{errors.description.message}</ErrorText>}
        </FieldGroup>
      </FormGrid>
    </Card>
  );
};
